import * as STD      from '@emersya/mpa-std-lib';
import * as BasketNS from './';

export type TOptionCustomizationTypes = 'image' | 'color' | 'text' | 'pattern';


export interface IOptionCustomization<TIsSerialized extends boolean> {
	readonly type : string;
	readonly id : string;

	// readonly pricing : BasketNS.Pricing.TPricing | null;

	readonly pricingInfo : {
		readonly pricing : TIsSerialized extends false ? BasketNS.Pricing.TPricing : BasketNS.Pricing.TPricingSerialized;
		/* If defined and not set to "exact", will round volume before using it to compute the price.*/
		readonly usedVolumeRoundingStrategy? : 'floor' | 'round' | 'ceil' | 'exact';
	} | null;

	readonly volumeComputation : {
		readonly boundTo : 'unit' |
			'surface-image-sqm' |
			'surface-image-m.height' |
			'surface-image-m.width' |
			'surface-patch-sqm' |
			'surface-patch-m.height' |
			'surface-patch-m.width';
		readonly roundingStrategy : 'floor' | 'round' | 'ceil' | 'none';
	} | null;

	volume : number;

	metaData? : STD.object.JSONComplexRecursive | null;
	computedPrice : TIsSerialized extends false ? BasketNS.Price : BasketNS.Price.ISerialized;
};

export type TOptionCustomizationMetaProperties = 'type' | 'id' | 'pricingInfo' | 'volumeComputation' | 'computedPrice';

//
// export interface IOptionCustomization {
// 	readonly type : string;
// 	readonly id : string;
//
// 	// readonly pricing : BasketNS.Pricing.TPricing | null;
//
// 	readonly pricingInfo : {
// 		readonly pricing : BasketNS.Pricing.TPricing;
// 		/* If defined and not set to "exact", will round volume before using it to compute the price.*/
// 		readonly usedVolumeRoundingStrategy? : 'floor' | 'round' | 'ceil' | 'exact';
// 	} | null;
//
// 	readonly volumeComputation : {
// 		readonly boundTo : 'unit' |
// 			'surface-image-sqm' |
// 			'surface-image-m.height' |
// 			'surface-image-m.width' |
// 			'surface-patch-sqm' |
// 			'surface-patch-m.height' |
// 			'surface-patch-m.width';
// 		readonly roundingStrategy : 'floor' | 'round' | 'ceil' | 'none';
// 	} | null;
//
// 	volume : number;
//
// 	metaData? : STD.object.JSONComplexRecursive | null;
// 	computedPrice : BasketNS.Price;
// };

export interface IOptionCustomizationImage<TIsSerialized extends boolean> extends IOptionCustomization<TIsSerialized> {
	readonly type : 'image';
	configurableMaterial : string;
	materialVariation : string;
	sourceFileURL : string;
	sourceFileType : string;
	productionFileURL : string;
	productionFileType : string;
	displayFileURL : string;
	rotation : number;
	position : [ number, number ];
	scaling : [ number, number ];
	crop : [ number, number ];
};

export interface IOptionCustomizationColor<TIsSerialized extends boolean> extends IOptionCustomization<TIsSerialized> {
	readonly  type : 'color';
};

export interface IOptionCustomizationText<TIsSerialized extends boolean> extends IOptionCustomization<TIsSerialized> {
	readonly type : 'text';
	configurableMaterial : string;
	materialVariation : string;
	text : string;
	font : string;
	size : string | number;
	color : string;
	colorType : string;
	italic : boolean;
	bold : boolean;
	underline : boolean;
	strokeText : boolean;
	strokeColor : string;
	strokeColorType : string;
	rotation : number;
	scaling : number;
	// verticalAlignment
	// horizontalAlignment
	// productionTechnique
	// offsetPosition
	// discount
	// technicalFees
	// productionFileURL : string; ??
	// productionFileType ??
};

export interface IOptionCustomizationPattern<TIsSerialized extends boolean> extends IOptionCustomization<TIsSerialized> {
	readonly type : 'pattern';
};
export type TOptionCustomization = (
	IOptionCustomizationImage<false> |
	IOptionCustomizationColor<false> |
	IOptionCustomizationText<false> |
	IOptionCustomizationPattern<false>
	);

export class Option {
	readonly part : BasketNS.Part;
	readonly id : string;
	readonly title : string; // the name...
	readonly reference : BasketNS.Reference | null = null;
	readonly technicalFees : BasketNS.Pricing.TPricing | null = null;
	readonly customImages = new Set<IOptionCustomizationImage<false>>;
	readonly customColors = new Set<IOptionCustomizationColor<false>>;
	readonly customTexts = new Set<IOptionCustomizationText<false>>;
	readonly customPatterns = new Set<IOptionCustomizationPattern<false>>;
	readonly components : BasketNS.Component[] = [];
	volume : number = 1;
	computedPrice : BasketNS.Price = BasketNS.Price.ofZero();
	metaData : STD.object.JSONComplexRecursive | null = null;
	// readonly pricing : BasketNS.Pricing.TPricing | null = null;

	readonly pricingInfo : {
		readonly pricing : BasketNS.Pricing.TPricing;
		readonly volumeComputationMethod : 'unit' | 'm' | 'm2' | 'm3';
	} | null;

	async dispatchCustomOptionUpdate(customOption : TOptionCustomization | null) {
		const product = this.part.getProduct();
		await product.basket.dispatchEvent('basket-update', {
			subType : 'part-customOption-updated',
			details : {
				product : product,
				part :    this.part,
				option :  this,
				customOption
			}
		});
	};

	// discount       : this.createDiscount(params.discount);
	getCustom(type : 'image', id : string) : IOptionCustomizationImage<false>;
	getCustom(type : 'color', id : string) : IOptionCustomizationColor<false>;
	getCustom(type : 'text', id : string) : IOptionCustomizationText<false>;
	getCustom(type : 'pattern', id : string) : IOptionCustomizationPattern<false>;
	// getCustom(
	// 	type : TOptionCustomizationTypes,
	// 	id : string
	// ) : TOptionCustomization
	getCustom(
		type : string,
		id : string
	) : IOptionCustomization<false>
	{
		const set = (
			(type === 'image') ? this.customImages :
				(type === 'color') ? this.customColors :
					(type === 'text') ? this.customTexts :
						(type === 'pattern') ? this.customPatterns :
							null
		);
		if (!set) {
			throw STD.errors.library('NOT FOUND', {
				type,
				id
			});
		}
		for (const x of set) {
			if (x.id === id) {
				return x;
			}
		}
		throw STD.errors.library('NOT FOUND', {
			type,
			id,
			set
		});
	};

	hasCustom(
		type : TOptionCustomizationTypes,
		id : string
	) : boolean {
		const set = (
			(type === 'image') ? this.customImages :
				(type === 'color') ? this.customColors :
					(type === 'text') ? this.customTexts :
						(type === 'pattern') ? this.customPatterns : null
		);
		if (!set) {
			throw STD.errors.library('NOT FOUND', {});
		}
		for (const x of set) {
			if (x.id === id) {
				return true;
			}
		}
		return false;
	};

	async addCustom(customization : Omit<TOptionCustomization, 'computedPrice'>) : Promise<void> {
		const c = <TOptionCustomization> customization;
		c.computedPrice = BasketNS.Price.ofZero();
		if (customization.type === 'image') {
			this.customImages.add(<any> customization);
		} else if (customization.type === 'color') {
			this.customColors.add(<any> customization);
		} else if (customization.type === 'text') {
			this.customTexts.add(<any> customization);
		} else if (customization.type === 'pattern') {
			this.customPatterns.add(<any> customization);
		}
		const product = this.part.getProduct();
		await product.basket.dispatchEvent('basket-update', {
			subType : 'part-customOption-add',
			details : {
				product :      product,
				part :         this.part,
				option :       this,
				customOption : customization
			}
		});
	};

	async removeCustom(type : TOptionCustomizationTypes, id : string) {
		let customOption;
		if (type === 'image') {
			const customOption = this.getCustom(type, id);
			this.customImages.delete(customOption);
		} else {
			throw STD.errors.user('Not implemented!', {
				type,
				id,
				option : this
			});
		}
		const product = this.part.getProduct();
		await product.basket.dispatchEvent('basket-update', {
			subType : 'part-customOption-remove',
			details : {
				product :      product,
				part :         this.part,
				option :       this,
				customOption : customOption
			}
		});
	};


	private updateCustomImage(
		customOption : IOptionCustomizationImage<false>,
		customization : Partial<Omit<IOptionCustomizationImage<false>, TOptionCustomizationMetaProperties>>
	)
	{
		if (customization.volume !== undefined) {
			customOption.volume = customization.volume;
		}
		if (customization.configurableMaterial) {
			customOption.configurableMaterial = customization.configurableMaterial;
		}
		if (customization.materialVariation) {
			customOption.materialVariation = customization.materialVariation;
		}
		if (customization.sourceFileURL) {
			customOption.sourceFileURL = customization.sourceFileURL;
		}
		if (customization.sourceFileType) {
			customOption.sourceFileType = customization.sourceFileType;
		}
		if (customization.productionFileURL) {
			customOption.productionFileURL = customization.productionFileURL;
		}
		if (customization.productionFileType) {
			customOption.productionFileType = customization.productionFileType;
		}
		if (customization.displayFileURL) {
			customOption.displayFileURL = customization.displayFileURL;
		}
		if (customization.rotation !== undefined) {
			customOption.rotation = customization.rotation;
		}
		if (customization.position) {
			customOption.position = customization.position;
		}
		if (customization.scaling) {
			customOption.scaling = customization.scaling;
		}
		if (customization.crop) {
			customOption.crop = customization.crop;
		}
		if (customization.metaData) {
			customOption.metaData = Object.assign({}, customOption.metaData, customization.metaData);
		}
	};

	private updateCustomColor(
		customOption : IOptionCustomizationColor<false>,
		customization : Partial<Omit<IOptionCustomizationColor<false>, TOptionCustomizationMetaProperties>>
	)
	{
		void customOption;
		void customization;
	};

	private updateCustomText(
		customOption : IOptionCustomizationText<false>,
		customization : Partial<Omit<IOptionCustomizationText<false>, TOptionCustomizationMetaProperties>>
	)
	{
		if (customization.configurableMaterial) {
			customOption.configurableMaterial = customization.configurableMaterial;
		}
		if (customization.materialVariation) {
			customOption.materialVariation = customization.materialVariation;
		}
		if (customization.text !== undefined) {
			customOption.text = customization.text;
		}
		if (customization.font) {
			customOption.font = customization.font;
		}
		if (customization.size) {
			customOption.size = customization.size;
		}
		if (customization.color) {
			customOption.color = customization.color;
		}
		if (customization.colorType) {
			customOption.colorType = customization.colorType;
		}
		if (customization.italic !== undefined) {
			customOption.italic = customization.italic;
		}
		if (customization.bold !== undefined) {
			customOption.bold = customization.bold;
		}
		if (customization.underline !== undefined) {
			customOption.underline = customization.underline;
		}
		if (customization.strokeText !== undefined) {
			customOption.strokeText = customization.strokeText;
		}
		if (customization.strokeColor) {
			customOption.strokeColor = customization.strokeColor;
		}
		if (customization.strokeColorType) {
			customOption.strokeColorType = customization.strokeColorType;
		}
		if (customization.rotation !== undefined) {
			customOption.rotation = customization.rotation;
		}
		if (customization.scaling !== undefined) {
			customOption.scaling = customization.scaling;
		}
		if (customization.metaData) {
			customOption.metaData = Object.assign({}, customOption.metaData, customization.metaData);
		}
	};

	private updateCustomPattern(
		customOption : IOptionCustomizationPattern<false>,
		customization : Partial<Omit<IOptionCustomizationPattern<false>, TOptionCustomizationMetaProperties>>
	)
	{
		void customOption;
		void customization;
	};

	async updateCustom(customization : TOptionCustomization) : Promise<void> {
		const customOption = <TOptionCustomization> this.getCustom(<any> customization.type, customization.id);
		// const customOption = <any> this.getCustom(<any> customization.type, customization.id);
		if (customOption.type === 'image') {
			this.updateCustomImage(customOption, <any> customization);
		} else if (customOption.type === 'color') {
			this.updateCustomColor(customOption, <any> customization);
		} else if (customOption.type === 'text') {
			this.updateCustomText(customOption, <any> customization);
		} else if (customOption.type === 'pattern') {
			this.updateCustomPattern(customOption, <any> customization);
		}
		await this.dispatchCustomOptionUpdate(customOption);
	};

	async updateVolume(newVolume : number) {
		this.volume = newVolume;
		const product = this.part.getProduct();
		await product.basket.dispatchEvent('basket-update', {
			subType : 'option-volume-updated',
			details : {
				product,
				newVolume,
				option : this,
				part :   this.part
			}
		});
	};

	serialize() : Option.ISerialized {
		const TF = this.technicalFees?.resolve() || null;
		return {
			name :           this.id,
			title :          this.title,
			components :     this.components.map(x => x.serialize()),
			volume :         this.volume,
			price :          this.computedPrice.serialize(),
			pricingInfo :    this.pricingInfo ? {
				pricing :                 this.pricingInfo.pricing.serialize(),
				volumeComputationMethod : this.pricingInfo.volumeComputationMethod
			} : null,
			metaData :       this.metaData ? this.metaData : null,
			reference :      this.reference ? this.reference.serialize() : null,
			technicalFees :  TF && !TF.isNull() ? TF.serialize() : null,
			customImages :   Array.from(this.customImages).map(x => JSON.parse(JSON.stringify(x))),
			customColors :   Array.from(this.customColors).map(x => JSON.parse(JSON.stringify(x))),
			customTexts :
											 Array.from(this.customTexts).map(x => JSON.parse(JSON.stringify(x))),
			customPatterns : Array.from(this.customPatterns).map(x => JSON.parse(JSON.stringify(x)))
		};
	};

	computePrice() {
		const computedPrice = this.pricingInfo ? this.pricingInfo.pricing.resolve().clone() : BasketNS.Price.ofZero();
		const customOptions : IOptionCustomization<false>[] = [
			...this.customImages,
			...this.customPatterns,
			...this.customColors,
			...this.customTexts
		];
		try {
			computedPrice.exclTax = computedPrice.exclTax.multipliedBy(this.volume);
		} catch (e) {
			throw e;
		}
		computedPrice.inclTax = computedPrice.inclTax.multipliedBy(this.volume);
		for (const customOption of customOptions) {
			let price;
			if (customOption.pricingInfo) {
				const basePrice = customOption.pricingInfo.pricing.resolve().clone();
				let volume = customOption.volume || 1;
				if (customOption.pricingInfo?.usedVolumeRoundingStrategy) {
					if (customOption.pricingInfo.usedVolumeRoundingStrategy === 'floor') {
						volume = Math.floor(volume);
					} else if (customOption.pricingInfo.usedVolumeRoundingStrategy === 'round') {
						volume = Math.round(volume);
					} else if (customOption.pricingInfo.usedVolumeRoundingStrategy === 'ceil') {
						volume = Math.ceil(volume);
					}
				}
				price = BasketNS.Price.fromExclTax(
					basePrice.exclTax.multipliedBy(volume),
					basePrice.taxes
				);
			} else {
				price = BasketNS.Price.ofZero();
			}
			customOption.computedPrice = price;
			computedPrice.add(price);
		}
		this.computedPrice = computedPrice;
		return (computedPrice);
	};

	constructor(desc : Option.IDescriptor) {
		this.part = desc.part;
		this.id = desc.id;
		this.title = desc.title;
		this.pricingInfo = desc.pricingInfo;
		if (desc.technicalFees) {
			this.technicalFees = desc.technicalFees;
		}
		if (desc.reference) {
			if (desc.reference instanceof BasketNS.Reference) {
				this.reference = desc.reference;
			} else {
				this.reference = new BasketNS.Reference(desc.reference.value);
			}
		}
		if (desc.components) {
			for (const componentDesc of desc.components) {
				this.createComponent(componentDesc);
			}
		}
		if (desc.metaData) {
			this.metaData = desc.metaData;
		}
		if (this.pricingInfo && this.pricingInfo.pricing instanceof BasketNS.Pricing.BoundPricing) {
			this.pricingInfo.pricing._host = this;
		}
	};

	createComponent(desc : Omit<BasketNS.IComponentDescriptor, 'option'>) {
		const component = new BasketNS.Component(Object.assign({}, desc, {
			option : this
		}));
		this.components.push(component);
	};
};
export namespace Option {
	export interface IDescriptor {
		readonly part : BasketNS.Part;
		readonly id : string;
		readonly title : string;
		readonly reference : BasketNS.Reference | BasketNS.Reference.ISerialized | null;
		readonly pricingInfo : {
			readonly pricing : BasketNS.Pricing.TPricing;
			readonly volumeComputationMethod : 'unit' | 'm' | 'm2' | 'm3';
		} | null;
		readonly technicalFees : BasketNS.Pricing.TPricing | null;
		readonly metaData : STD.object.JSONComplexRecursive | null;
		readonly components : Omit<BasketNS.IComponentDescriptor, 'option'>[];
	};

	export interface ISerialized {
		readonly price : BasketNS.Price.ISerialized; // => computed price
		readonly pricingInfo : {
			readonly pricing : BasketNS.Pricing.TPricingSerialized;
			readonly volumeComputationMethod : 'unit' | 'm' | 'm2' | 'm3';
		} | null;


		readonly name : string; // id ;(
		readonly title : string;
		readonly volume : number;
		readonly components : BasketNS.IComponentSerialized[];
		readonly metaData : STD.object.JSONComplexRecursive | null;
		readonly reference : BasketNS.Reference.ISerialized | null;
		readonly technicalFees : BasketNS.Price.ISerialized | null;
		readonly customImages : IOptionCustomizationImage<true>[];
		readonly customColors : IOptionCustomizationColor<true>[];
		readonly customTexts : IOptionCustomizationText<true>[];
		readonly customPatterns : IOptionCustomizationPattern<true>[];
	};
}
